import { stringify } from 'query-string';
import { CALL_API } from 'redux-api-middleware';
import {
    getDefaultList,
    getDefaultMarket,
    getListById,
    getMarketById,
    getRegionById,
} from './stocks';

import config from '../config';

const host = config.oms.host;
const baseUrl = `https://${host}/server`;
const baseUrlSecure = `${baseUrl}/secure`;

const PERIOD_INTRADAY = 'PERIOD_INTRADAY';
const PERIOD_HISTORIC = 'PERIOD_HISTORIC';

function buildApiAction(definition, sig = null) {
    const secureDef = sig ? {
        credentials: 'include',
        headers: { 'X-SPID-Signature': sig },
    } : {};

    return {
        [CALL_API]: {
            method: 'GET',

            ...secureDef,
            ...definition,
        },
    };
}

function buildGraphDataUrl(symbol, period) {
    // Some symbols must be double encoded, e.g. 'C:PBROUSDBR\SP.IDCENE'.
    const encodedSymbol = encodeURIComponent(encodeURIComponent(symbol));
    const base = `${baseUrl}/components/graphdata`;

    if (period === PERIOD_INTRADAY) {
        return {
            graph: `${base}/s1/tick/${encodedSymbol}?points=300&period=1opendays`,
            volume: `${base}/(VOLUME)/tick/${encodedSymbol}?resolution=50`,
        };
    } else if (period === PERIOD_HISTORIC) {
        return {
            graph: `${base}/(CLOSE_CA)/DAY/${encodedSymbol}?points=3000&period=10years`,
            volume: `${base}/(VOLUME)/day/${encodedSymbol}?resolution=10&period=10years`,
        };
    }

    throw new Error(`Unkown period: '${period}'`);
}

function buildComponentUrl({ symbol, columns = [] } = {}, { secure = false } = {}) {
    const base = `${secure ? baseUrlSecure : baseUrl}/components`;

    const queryString = stringify({
        itemSector: symbol.toUpperCase(),
        columns: columns.join(','),
    });

    return `${base}?${queryString}`;
}

function buildCalendarUrl(ticker) {
    const url = `${baseUrl}/components`;

    const filter = ticker ? `TICKER%%s^(.*,)?${ticker}(,.*)?$` : 'ITEM==sAll';
    const limit = ticker ? 365 : 7;

    const date = new Date();
    date.setHours(0, 0, 0, 0);

    const queryString = stringify({
        columns: [
            'TICKER',
            'CALENDAR_EVENT_DATE',
            'CALENDAR_EVENT_HEADING_NO',
            'CALENDAR_EVENT_ID',
            'ose.LONG_NAME as OSE_LONG_NAME',
            'ose.ITEM_SECTOR as OSE_ITEM_SECTOR',
            'oax.LONG_NAME as OAX_LONG_NAME',
            'oax.ITEM_SECTOR as OAX_ITEM_SECTOR',
            'merk.LONG_NAME as MERK_LONG_NAME',
            'merk.ITEM_SECTOR as MERK_ITEM_SECTOR',
        ].join(','),
        leftJoins: `feed.ose.quotes.EQUITIES+PCC+ETPS+RIGHTS+WARRANTS:DELAYED as ose on TICKER,
            feed.oax.quotes.EQUITIES+RIGHTS:DELAYED as oax on TICKER,
            feed.merk.quotes.EQUITIES+PCC+RIGHTS:DELAYED as merk on TICKER`,
        filter,
        source: 'feed.calendar.ALL',
        type: 'history',
        stop: date.setDate(date.getDate() + limit),
        start: new Date().setHours(0, 0, 0, 0),
    });

    return `${url}?${queryString}`;
}

function buildNoticesUrl(ticker, limit = 3) {
    const url = `${baseUrl}/components`;

    const filter = ticker ? `COMPANY_TICKER%%s^(.*,)?${ticker}(,.*)?$` : 'ITEM==sAll';

    let leftJoins = null;

    const columns = [
        'TIME', 'REFERENCEID', 'COMPANY_TICKER', 'NEWSTYPE', 'HEADLINE', 'TEXT',
    ];

    if (!ticker) {
        columns.push(
            'ose.LONG_NAME as OSE_LONG_NAME',
            'ose.ITEM_SECTOR as OSE_ITEM_SECTOR',
            'oax.LONG_NAME as OAX_LONG_NAME',
            'oax.ITEM_SECTOR as OAX_ITEM_SECTOR',
            'merk.LONG_NAME as MERK_LONG_NAME',
            'merk.ITEM_SECTOR as MERK_ITEM_SECTOR',
        );

        leftJoins =
            `feed.ose.quotes.EQUITIES+PCC+ETPS+RIGHTS+WARRANTS:DELAYED as ose on COMPANY_TICKER,
            feed.oax.quotes.EQUITIES+RIGHTS:DELAYED as oax on COMPANY_TICKER,
            feed.merk.quotes.EQUITIES+PCC+RIGHTS:DELAYED as merk on COMPANY_TICKER`;
    }

    const query = {
        columns: columns.join(','),
        leftJoins,
        type: 'history',
        source: 'feed.news.ob.ALL',
        filter,
        stop: 'now',
        limit,
    };

    return `${url}?${stringify(query)}`;
}

function buildTradeUrl(ticker, market, limit = 5) {
    const url = `${baseUrl}/components`;

    const sources = {
        OSE: 'feed.ose.trades.INSTRUMENTS',
        OAX: 'feed.oax.trades.INSTRUMENTS',
        MERK: 'feed.merk.trades.INSTRUMENTS',
    };

    const queryString = stringify({
        type: 'history',
        source: sources[market],
        filter: `ITEM==s${ticker}`,
        columns: [
            'ID', 'TIME', 'PRICE', 'VOLUME', 'SELLER', 'BUYER', 'TYPE',
        ].join(','),
        limit,
        start: new Date().setHours(0, 0, 0, 0),
    });

    return `${url}?${queryString}`;
}

function buildInsidersUrl(ticker, market) {
    const url = `${baseUrl}/components`;

    const sources = {
        OSE: 'feed.ose.quotes.EQUITIES',
        OAX: 'feed.oax.quotes.EQUITIES',
        MERK: 'feed.merk.quotes.EQUITIES',
    };

    const queryString = stringify({
        type: 'ranking',
        source: sources[market],
        ranking: '-IMPORTANCE_',
        filter: `ITEM==s${ticker}`,
        convert: 'levels',
        view: 'DELAYED',
        columns: [
            'FIRST_NAME_', 'LAST_NAME_', 'TITLE_', 'COMMISSION_', 'IMPORTANCE_',
        ].join(','),
        limit: 50,
    });

    return `${url}?${queryString}`;
}

function buildBrokersUrl(ticker, market) {
    const url = `${baseUrl}/components`;

    const sources = {
        OSE: 'brokerstats.ose.tickerbroker.TICKERBROKERS',
        OAX: 'brokerstats.oax.tickerbroker.TICKERBROKERS',
        MERK: 'brokerstats.merk.tickerbroker.TICKERBROKERS',
    };

    const leftJoins = {
        OSE: 'feed.ose.brokers.BROKERS',
        OAX: 'feed.oax.brokers.BROKERS',
        MERK: 'feed.merk.brokers.BROKERS',
    };

    const queryString = stringify({
        type: 'ranking',
        ranking: '-TRADES_TURNOVER',
        source: `${sources[market]} as stats`,
        leftJoins: `${leftJoins[market]} as brokers on BROKER_ID`,
        filter: `ITEM%%s^${ticker}_.*`,
        view: 'DELAYED',
        columns: [
            'BROKER_ID',
            'TRADES_COUNT',
            'TRADES_COUNT_BUYS',
            'TRADES_COUNT_SELLS',
            'TRADES_PRICE_AVERAGE',
            'TRADES_PRICE_AVERAGE_BUYS',
            'TRADES_PRICE_AVERAGE_SELLS',
            'TRADES_TURNOVER',
            'TRADES_TURNOVER_BUYS',
            'TRADES_TURNOVER_SELLS',
            'expression.MEMBER_NAME',
        ].join(','),
        convert: 'expression',
        // eslint-disable-next-line
        expression: 'MEMBER_NAME = (brokers.MEMBER_NAME == null) ? stats.BROKER_ID : brokers.MEMBER_NAME',
    });

    return `${url}?${queryString}`;
}

function buildDividendsUrl(symbol) {
    const url = `${baseUrl}/components`;

    const queryString = stringify({
        type: 'history',
        source: 'feed.dividends.EQUITIES',
        filter: `ITEM==s${symbol}&&SEARCH_HISTORY==n1&&SEARCH_HISTORY_COUNT==n100`,
        columns: [
            'DIVIDEND_EX_DATE', 'DIVIDEND_RATE', 'DIVIDEND_CURRENCY',
        ].join(','),
        space: 'DAY',
        stop: 'now',
        limit: 100,
    });

    return `${url}?${queryString}`;
}

function buildCurrenciesUrl() {
    const url = `${baseUrl}/components`;

    const queryString = stringify({
        'generators[0][source]': 'feed.omni.index.CURRENCIES_SEK_RT',
        'generators[1][source]': 'feed.omni.index.CURRENCIES_SEK_EOD',
        columns: [
            'ITEM_SECTOR',
            'LONG_NAME',
        ].join(','),
        ranking: 'LONG_NAME',
    });

    return `${url}?${queryString}`;
}

function buildCompanyUrl(ticker, market) {
    const url = `${baseUrl}/components`;

    const sources = {
        OSE: 'feed.ose.quotes.EQUITIES',
        OAX: 'feed.oax.quotes.EQUITIES',
        MERK: 'feed.merk.quotes.EQUITIES',
    };

    const queryString = stringify({
        type: 'table',
        source: `${sources[market]} as quote`,
        filter: `ITEM==s${ticker}`,
        // eslint-disable-next-line
        leftJoins: 'feed.ob.companyinfo.ORGANISATION_NUMBERS:REALTIME as info on quote.ORGANISATION_NUMBER',
        columns: [
            'ORGANISATION_NUMBER',
            'info.LOGO_FILENAME',
            'info.DESCRIPTION_NO',
            'info.COMPANY_NAME',
            'info.COMPANY_URL',
            'info.TELEPHONE',
        ].join(','),
    });

    return `${url}?${queryString}`;
}

function buildOrdersUrl(ticker, market) {
    const url = `${baseUrl}/components`;

    const sources = {
        OSE: 'feed.ose.orders.EQUITIES',
        OAX: 'feed.oax.orders.EQUITIES',
        MERK: 'feed.merk.orders.EQUITIES',
    };

    const queryString = stringify({
        type: 'table',
        convert: 'levels',
        source: sources[market],
        columns: [
            'level', 'ASK', 'BID', 'ASKVOL', 'BIDVOL',
        ].join(','),
        limit: 5,
        view: 'DELAYED',
        filter: `ITEM==s${ticker}`,
    });

    return `${url}?${queryString}`;
}

function buildSearchUrl(query) {
    const url = `${baseUrl}/suggest`;

    const queryString = stringify({
        group: 'SEARCH_TICKERS',
        limit: 20,
        query,
    });

    return `${url}?${queryString}`;
}

const rankingParams = {
    type: 'ranking',
    limit: 10,
    columns: [
        'CHANGE_PCT',
        'expression.LASTNZ',
        'ITEM_SECTOR',
        'LONG_NAME',
        'SHORT_NAME',
        'TURNOVER',
        'TURNOVER_MAX',
        'TURNOVER_TOTAL',
        'VOLUME',
    ].join(','),
    convert: 'expression',
    cutoffAtZero: true,
    expression: 'LASTNZ = source.LASTNZ == null ? source.LASTNZ_DIV : source.LASTNZ',
};

const winnerParams = {
    ranking: '-CHANGE_PCT',
    ...rankingParams,
};

const loserParams = {
    ranking: 'CHANGE_PCT',
    ...rankingParams,
};

const turnoverParams = {
    ranking: '-TURNOVER',
    ...rankingParams,
};

const turnoverTotalParams = {
    ranking: '-TURNOVER_TOTAL',
    ...rankingParams,
};

const largestTradesParams = {
    ranking: '-TURNOVER_MAX',
    ...rankingParams,
};

function getRankingParams(feed, ranking) {
    const map = {
        winners: {
            ...winnerParams,
            'generators[0][source]': `${feed} as source`,
        },
        losers: {
            ...loserParams,
            'generators[0][source]': `${feed} as source`,
        },
        turnover: {
            ...turnoverParams,
            'generators[0][source]': `${feed} as source`,
        },
        largest_trades: {
            ...largestTradesParams,
            'generators[0][source]': `${feed} as source`,
        },
        total_turnover: {
            ...turnoverTotalParams,
            'generators[0][source]': `${feed} as source`,
        },
    };

    if (!map.hasOwnProperty(ranking)) {
        throw new Error(`Unkown ranking: ${ranking}`);
    }

    return map[ranking];
}

function buildRankingUrl(market, ranking, { secure = false } = {}) {
    const list = getDefaultList(market.id);
    const feed = `${market.feed}.${list.source}`;
    const base = `${secure ? baseUrlSecure : baseUrl}/components`;
    const params = getRankingParams(feed, ranking);

    return `${base}?${stringify(params, { arrayFormat: 'index' })}`;
}

// @FIXME This function isn't very pretty. Please fix it.
function getStocksParams(regionId, filters) {
    const region = getRegionById(regionId);
    const { list = null, market = null, sort = null } = filters;

    const params = {
        type: 'ranking',
        columns: [
            'LAST',
            'LASTNZ',
            'LASTNZ_DIV',
            'ITEM_SECTOR',
        ],
        convert: 'expression',
        cutoffAtZero: true,
        expression: 'LASTNZ = source.LASTNZ == null ? source.LASTNZ_DIV : source.LASTNZ',
    };

    let currentMarket;
    let currentList;

    // Figuring out which list and market to use
    if (list && market) {
        currentMarket = getMarketById(market);
        currentList = getListById(market, list);
    } else if (market) {
        currentMarket = getMarketById(market);
        currentList = getDefaultList(market);
    } else {
        currentMarket = getDefaultMarket(region.id);
        currentList = getDefaultList(currentMarket.id);
    }

    // Build the source based on market and feed from the selectedTickers
    const source = [
        currentMarket.feed,
        currentList.source,
    ].join('.');

    // In order for the expression to work, we'll need to use the generators
    // source instead of regular source
    params['generators[0][source]'] = `${source} as source`;

    // If the selected list contains further filters, we'll have to add a
    // filter parameter to the request. This is typically when we deal with
    // combined lists, e.g. EQUITIES+PCC+WARRANTS.
    // In these cases the filter will look like this: INSTRUMENT_TYPE=sEQUITIES,
    // which will return only the EQUITIES from the feed.
    if (currentList.filter) {
        params.columns.push('INTRUMENT_TYPE');
        params.filter = `INSTRUMENT_TYPE=s${currentList.filter}`;
    }

    if (sort) {
        // The ranking type of the API have a sort option, where "-" is the symbol
        // for descending ordering of the tickers. Ascending order isn't prepended
        // by anything. Thus, we'll have to prepend a "-" to the column name.
        const sortColumn = sort.column || 'LONG_NAME';
        const sortDirection = (sort.direction && sort.direction === 'DESC') ? '-' : '';

        params.ranking = sortDirection + sortColumn;
        params.columns.push(sortColumn);
    }

    params.columns = params.columns.join(',');

    return params;
}

function buildStocksUrl(regionId, filters, { secure = false } = {}) {
    const base = `${secure ? baseUrlSecure : baseUrl}/components`;
    const params = getStocksParams(regionId, filters);

    return `${base}?${stringify(params, { arrayFormat: 'index' })}`;
}

/**
 * Build websocket URL.
 *
 * Given the `secure` param, the URL will be an URL which accept Spid authentication. This
 * requires a valid Spid session. This allows for real time stock updates.
 *
 * Non-authenticated users should be given the non-secure URL (default).
 */
function buildSocketUrl({ secure = false } = {}) {
    return secure ? `wss://${host}/server/secure/components` : `wss://${host}/server/components`;
}

function buildRestUrl({ secure = false } = {}) {
    return secure ? `https://${host}/server/secure/components` : `https://${host}/server/components`;
}

export {
    buildApiAction,
    buildBrokersUrl,
    buildCalendarUrl,
    buildCompanyUrl,
    buildComponentUrl,
    buildDividendsUrl,
    buildCurrenciesUrl,
    buildGraphDataUrl,
    buildInsidersUrl,
    buildNoticesUrl,
    buildOrdersUrl,
    buildRankingUrl,
    buildRestUrl,
    buildSearchUrl,
    buildSocketUrl,
    buildStocksUrl,
    buildTradeUrl,
    getRankingParams,
    PERIOD_INTRADAY,
    PERIOD_HISTORIC,
};



/** WEBPACK FOOTER **
 ** /src/shared/service/oms.js
 **/